import React, { useEffect, useState } from "react";
import { getCitiesByState } from "../../../Services/GetLocation";
import {
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";

export const LocationForm = ({ locations, setLocations, disabled }) => {
  const [countriesData, setCountriesData] = useState([]);
  const [statesData, setStatesData] = useState({});
  const [cityDataList, setCityDataList] = useState([]);

  // Fetch countries & states on load
  useEffect(() => {
    (async () => {
      const response = await fetch(
        "https://countriesnow.space/api/v0.1/countries/states"
      );
      const data = await response.json();
      setCountriesData(data.data);

      const stateMap = {};
      data.data.forEach((c) => (stateMap[c.name] = c.states));
      setStatesData(stateMap);
    })();
  }, []);

  // Fetch cities dynamically
  useEffect(() => {
    const fetchCities = async () => {
      const results = await Promise.all(
        locations.map(async (loc) => {
          if (loc.country && loc.state) {
            try {
              const cities = await getCitiesByState(loc.country, loc.state);
              return Array.isArray(cities) ? cities : [];
            } catch {
              return [];
            }
          }
          return [];
        })
      );
      setCityDataList(results);
    };
    fetchCities();
  }, [locations]);

  // Handlers
  const handleChange = (index, field, value) => {
    if (disabled) return;
    const updated = [...locations];
    updated[index][field] = value;

    // Reset dependent fields
    if (field === "country") {
      updated[index].state = "";
      updated[index].city = "";
      updated[index].locationType = "";
    }
    if (field === "state") {
      updated[index].city = "";
    }

    setLocations(updated);
  };

  const addLocation = () => {
    if (!disabled) {
      setLocations([
        ...locations,
        { country: "", city: "", state: "", address: "", locationType: "" },
      ]);
      setCityDataList([...cityDataList, []]);
    }
  };

  const normalizeCity = (city) =>city?.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const removeLocation = (index) => {
    if (!disabled) {
      setLocations(locations.filter((_, i) => i !== index));
      setCityDataList(cityDataList.filter((_, i) => i !== index));
    }
  };

  return (
    <Paper elevation={2} sx={{ padding: 2, borderRadius: 3 }}>
      <Typography variant="h6" color={"#286a94"} fontWeight={600} mb={1}>
        Company Locations
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {locations.map((loc, index) => (
        <Paper
          key={index}
          elevation={1}
          sx={{ p: 3, mb: 2, borderRadius: 2, background: "#fafafa" }}
        >
          <Typography
            variant="subtitle1"
            textTransform={"uppercase"}
            fontWeight={700}
            mb={2}
            color={"#286a94"}
          >
            {loc.locationType ? loc.locationType + " Office" : null}
          </Typography>
          <Grid container spacing={2}>
            {/* Country */}
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                label="Country"
                value={loc.country}
                onChange={(e) => handleChange(index, "country", e.target.value)}
                disabled={disabled}
              >
                <MenuItem value="">Select Country</MenuItem>
                {countriesData.map((c) => (
                  <MenuItem key={c.iso2} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* State */}
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                label="State"
                value={loc.state}
                disabled={!loc.country || disabled}
                onChange={(e) => handleChange(index, "state", e.target.value)}
              >
                <MenuItem value="">Select State</MenuItem>
                {statesData[loc.country]?.map((s) => (
                  <MenuItem key={s.state_code || s.name} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* City */}
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                label="City"
                value={loc.city}
                disabled={!loc.state || disabled}
                onChange={(e) => handleChange(index, "city", e.target.value)}
              >
                <MenuItem value="">Select City</MenuItem>
                {/* {(cityDataList[index] || []).map((city, i) => (
                  <MenuItem key={i} value={city}>
                    {city}
                  </MenuItem>
                ))} */}
                {[
                  ...new Map(
                    (cityDataList[index] || []).map((city) => [
                      normalizeCity(city),
                      city,
                    ])
                  ).entries(),
                ].map(([normalized, original]) => (
                  <MenuItem key={normalized} value={original}>
                    {normalized}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Address */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Address"
                name="address"
                value={loc.address}
                disabled={disabled}
                onChange={(e) => handleChange(index, "address", e.target.value)}
              />
            </Grid>

            {/* Type */}
            <Grid item xs={12} md={5}>
              <TextField
                select
                fullWidth
                required
                label="Address Type"
                value={loc.locationType}
                disabled={disabled}
                onChange={(e) =>
                  handleChange(index, "locationType", e.target.value)
                }
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="HEADQUARTERS">Headquarters</MenuItem>
                <MenuItem value="BRANCH">Branch</MenuItem>
              </TextField>
            </Grid>

            {/* Remove Button */}
            <Grid item xs={12} md={1} align="right">
              {locations.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => removeLocation(index)}
                  disabled={disabled}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Add Location Button */}
      <Button
        startIcon={<AddLocationAltIcon />}
        variant="outlined"
        style={{ color: "#286a94" }}
        onClick={addLocation}
        disabled={disabled}
      >
        Add Location
      </Button>
    </Paper>
  );
};
